import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, Settings, Plus, Edit2, Trash2, Save, X,
  AlertTriangle, CheckCircle, RefreshCw, Sliders, Database
} from 'lucide-react';

const API_URL = getApiBaseUrl();

const AIPolicyMatrix = () => {
  const [rules, setRules] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  const [filter, setFilter] = useState({ domain: '', isActive: '' });

  // Form state for create/edit
  const [formData, setFormData] = useState({
    domain: 'screening',
    action: 'approve',
    risk_class: 'medium',
    confidence_floor: 0.75,
    min_autonomy_level: 'L2',
    required_evidence: [],
    queue_route: '',
    review_timeout_hours: 24,
    is_active: true,
  });

  const domains = ['screening', 'arrears', 'maintenance', 'comms', 'legal'];
  const riskClasses = ['low', 'medium', 'high', 'critical'];
  const autonomyLevels = ['L0', 'L1', 'L2', 'L3', 'L4'];

  // Get workspace and org from user context
  const getWorkspaceOrg = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      workspace_id: user.workspace_id || '00000000-0000-0000-0000-000000000000',
      org_id: user.org_id || '00000000-0000-0000-0000-000000000000',
    };
  };

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const { workspace_id, org_id } = getWorkspaceOrg();
      
      const params = new URLSearchParams({ workspace_id, org_id });
      if (filter.domain) params.append('domain', filter.domain);
      if (filter.isActive !== '') params.append('is_active', filter.isActive);

      const response = await fetch(`${API_URL}/api/ai/policy/rules?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchDefaults = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/ai/policy/defaults`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDefaults(data.defaults || []);
    } catch (error) {
      console.error('Failed to fetch defaults:', error);
    }
  };

  const seedDefaults = async () => {
    setSeedingDefaults(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const { workspace_id, org_id } = getWorkspaceOrg();
      
      const response = await fetch(
        `${API_URL}/api/ai/policy/rules/seed-defaults?workspace_id=${workspace_id}&org_id=${org_id}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      alert(`Seeded ${data.created} rules, skipped ${data.skipped} existing`);
      fetchRules();
    } catch (error) {
      console.error('Failed to seed defaults:', error);
      alert('Failed to seed default rules');
    } finally {
      setSeedingDefaults(false);
    }
  };

  const createRule = async () => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const { workspace_id, org_id } = getWorkspaceOrg();
      
      const response = await fetch(
        `${API_URL}/api/ai/policy/rules?workspace_id=${workspace_id}&org_id=${org_id}`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create rule');
      }
      
      setShowCreateForm(false);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Failed to create rule:', error);
      alert(error.message);
    }
  };

  const updateRule = async (ruleId) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/ai/policy/rules/${ruleId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          risk_class: formData.risk_class,
          confidence_floor: formData.confidence_floor,
          min_autonomy_level: formData.min_autonomy_level,
          required_evidence: formData.required_evidence,
          queue_route: formData.queue_route,
          review_timeout_hours: formData.review_timeout_hours,
          is_active: formData.is_active,
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update rule');
      }
      
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Failed to update rule:', error);
      alert(error.message);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/ai/policy/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete rule');
      }
      
      fetchRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      alert(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      domain: 'screening',
      action: 'approve',
      risk_class: 'medium',
      confidence_floor: 0.75,
      min_autonomy_level: 'L2',
      required_evidence: [],
      queue_route: '',
      review_timeout_hours: 24,
      is_active: true,
    });
  };

  const startEdit = (rule) => {
    setEditingRule(rule.id);
    setFormData({
      domain: rule.domain,
      action: rule.action,
      risk_class: rule.risk_class,
      confidence_floor: rule.confidence_floor,
      min_autonomy_level: rule.min_autonomy_level,
      required_evidence: rule.required_evidence || [],
      queue_route: rule.queue_route || '',
      review_timeout_hours: rule.review_timeout_hours,
      is_active: rule.is_active,
    });
  };

  useEffect(() => {
    fetchRules();
    fetchDefaults();
  }, [fetchRules]);

  const getRiskColor = (risk) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800',
    };
    return colors[risk] || 'bg-gray-100 text-gray-800';
  };

  const getAutonomyColor = (level) => {
    const colors = {
      'L0': 'bg-gray-100 text-gray-800',
      'L1': 'bg-blue-100 text-blue-800',
      'L2': 'bg-indigo-100 text-indigo-800',
      'L3': 'bg-purple-100 text-purple-800',
      'L4': 'bg-pink-100 text-pink-800',
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="ai-policy-matrix">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-6 w-6 text-indigo-600" />
              AI Policy Matrix
            </h1>
            <p className="text-gray-600 mt-1">
              Configure autonomy levels, confidence thresholds, and review requirements
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchRules}
              data-testid="refresh-rules-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button 
              variant="outline"
              onClick={seedDefaults}
              disabled={seedingDefaults}
              data-testid="seed-defaults-btn"
            >
              <Database className="h-4 w-4 mr-2" />
              {seedingDefaults ? 'Seeding...' : 'Seed Defaults'}
            </Button>
            <Button 
              onClick={() => { resetForm(); setShowCreateForm(true); }}
              data-testid="create-rule-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Rules</p>
                <p className="text-2xl font-bold">{rules.length}</p>
              </div>
              <Sliders className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Rules</p>
                <p className="text-2xl font-bold text-green-600">
                  {rules.filter(r => r.is_active).length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">High Risk Rules</p>
                <p className="text-2xl font-bold text-orange-600">
                  {rules.filter(r => r.risk_class === 'high' || r.risk_class === 'critical').length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Default Templates</p>
                <p className="text-2xl font-bold text-blue-600">{defaults.length}</p>
              </div>
              <Settings className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div>
              <label className="text-sm font-medium text-gray-700">Domain</label>
              <select
                value={filter.domain}
                onChange={(e) => setFilter({...filter, domain: e.target.value})}
                className="ml-2 border rounded px-3 py-1.5"
                data-testid="filter-domain"
              >
                <option value="">All Domains</option>
                {domains.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select
                value={filter.isActive}
                onChange={(e) => setFilter({...filter, isActive: e.target.value})}
                className="ml-2 border rounded px-3 py-1.5"
                data-testid="filter-status"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editingRule) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingRule ? 'Edit Rule' : 'Create New Rule'}</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { setShowCreateForm(false); setEditingRule(null); resetForm(); }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Domain</label>
                  <select
                    value={formData.domain}
                    onChange={(e) => setFormData({...formData, domain: e.target.value})}
                    className="w-full border rounded px-3 py-2 mt-1"
                    disabled={!!editingRule}
                    data-testid="form-domain"
                  >
                    {domains.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Action</label>
                  <input
                    type="text"
                    value={formData.action}
                    onChange={(e) => setFormData({...formData, action: e.target.value})}
                    className="w-full border rounded px-3 py-2 mt-1"
                    disabled={!!editingRule}
                    placeholder="approve, reject, escalate..."
                    data-testid="form-action"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Risk Class</label>
                  <select
                    value={formData.risk_class}
                    onChange={(e) => setFormData({...formData, risk_class: e.target.value})}
                    className="w-full border rounded px-3 py-2 mt-1"
                    data-testid="form-risk-class"
                  >
                    {riskClasses.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Min Autonomy Level</label>
                  <select
                    value={formData.min_autonomy_level}
                    onChange={(e) => setFormData({...formData, min_autonomy_level: e.target.value})}
                    className="w-full border rounded px-3 py-2 mt-1"
                    data-testid="form-autonomy-level"
                  >
                    {autonomyLevels.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Confidence Floor: {(formData.confidence_floor * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={formData.confidence_floor * 100}
                  onChange={(e) => setFormData({...formData, confidence_floor: parseInt(e.target.value) / 100})}
                  className="w-full mt-1"
                  data-testid="form-confidence-floor"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Decisions below this confidence will require human review
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Queue Route (optional)</label>
                <input
                  type="text"
                  value={formData.queue_route}
                  onChange={(e) => setFormData({...formData, queue_route: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1"
                  placeholder="screening_review, legal_review..."
                  data-testid="form-queue-route"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Review Timeout (hours)</label>
                  <input
                    type="number"
                    value={formData.review_timeout_hours}
                    onChange={(e) => setFormData({...formData, review_timeout_hours: parseInt(e.target.value)})}
                    className="w-full border rounded px-3 py-2 mt-1"
                    min="1"
                    max="168"
                    data-testid="form-timeout"
                  />
                </div>
                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 rounded"
                    data-testid="form-is-active"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">Active</label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline"
                  onClick={() => { setShowCreateForm(false); setEditingRule(null); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => editingRule ? updateRule(editingRule) : createRule()}
                  data-testid="form-submit-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingRule ? 'Update' : 'Create'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rules Table */}
      <Card>
        <CardHeader>
          <CardTitle>Policy Rules</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-500">Loading rules...</p>
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 mx-auto text-gray-300" />
              <p className="mt-2 text-gray-500">No rules found</p>
              <p className="text-sm text-gray-400">
                Click &quot;Seed Defaults&quot; to create default rules or &quot;Create Rule&quot; to add a custom one
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Domain</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Action</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Risk</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Confidence Floor</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Autonomy</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr key={rule.id} className="border-b hover:bg-gray-50" data-testid={`rule-row-${rule.id}`}>
                      <td className="py-3 px-4">
                        <span className="font-medium capitalize">{rule.domain}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-gray-600">{rule.action}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(rule.risk_class)}`}>
                          {rule.risk_class}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono">{(rule.confidence_floor * 100).toFixed(0)}%</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAutonomyColor(rule.min_autonomy_level)}`}>
                          {rule.min_autonomy_level}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {rule.is_active ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-4 w-4" /> Active
                          </span>
                        ) : (
                          <span className="text-gray-400 flex items-center gap-1">
                            <X className="h-4 w-4" /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => startEdit(rule)}
                          data-testid={`edit-rule-${rule.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteRule(rule.id)}
                          className="text-red-600 hover:text-red-800"
                          data-testid={`delete-rule-${rule.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIPolicyMatrix;
